<template>
  <div class="green-fitout green-fitout-comments">
    <div class="green-fitout__body">
      <template v-if="isHKlandUser">
        <HKLLeftRight>
          <div class="green-fitout-comments__title font-bold">
            {{ $t('tenantProjects.greenFitOut.titleReview') }}
          </div>
          <div
            v-if="isUnderReview"
            class="green-fitout-comments__review-status"
          >
            <HKLGrayHint
              v-if="!isCurrentReviewer && canViewContent"
            >
              {{ $t('tenantProjects.greenFitOut.underReviewBy', [currentReviewer.displayName]) }}
            </HKLGrayHint>
            <HKLGrayHint
              v-if="lastReviewedBy && canViewContent"
            >
              {{ $t('tenantProjects.greenFitOut.lastReviewedBy', [lastReviewedBy.displayName, lastReviewedBy.createdAt]) }}
            </HKLGrayHint>
            <HKLGrayHint
              v-if="canViewContent || shouldShowTips"
            >
              {{ $t('tenantProjects.submission.startedOn', [underReviewStartOn]) }}
            </HKLGrayHint>
          </div>
        </HKLLeftRight>
        <div
          v-if="canViewPoint"
          class="green-fitout-comments__title font-bold"
        >
          {{ $t('tenantProjects.greenFitOut.totalPoints', [noHasPoint ? $t('emptyValue') : totalPoints]) }}
        </div>
        <div
          v-if="shouldShowTips"
          class="row align-center"
        >
          <div class="tip">
            <span class="tip__text only-read">
              <template v-if="isUnderReview">
                {{ $t('tenantProjects.greenFitOut.underReviewBy', [currentReviewer.displayName]) }}
              </template>
              <template v-else>
                {{ $t('tenantProjects.greenFitOut.noStartLetter') }}
              </template>
            </span>
          </div>
        </div>
        <div class="row align-center">
          <template v-if="canViewContent">
            <HKLButton
              v-if="!isUnderReview && !isCompleted && canCommentOnGreenFitout"
              color="darkblue"
              :disabled="disableStartBtn"
              @click="startLetter"
            >
              {{ $t('tenantProjects.greenFitOut.startLetter') }}
            </HKLButton>
            <template v-if="isUnderReview">
              <DescriptionTinymceEditor
                v-if="isPaneActivated && isCurrentReviewer && canCommentOnGreenFitout"
                v-model="currentGreenFitout.comments.description"
                class="green-fitout-comments_description-input"
              />
              <p
                v-else
                class="green-fitout-comments_description-input-display"
                v-html="currentGreenFitout.comments.description"
              />
              <div class="green-fitout-comments__attachments">
                <HkldDropzone
                  v-if="isCurrentReviewer && canCommentOnGreenFitout"
                  :sourceId="currentGreenFitout.id"
                  :metaData="{uploadOnTop: true}"
                />
                <TenantSubmissionDocuments
                  v-bind="commentAttachmentAttrs"
                />
              </div>
            </template>
          </template>
        </div>
      </template>
      <GreenFitOutHistory />
    </div>
    <div
      v-if="isHKlandUser"
      class="on-hold-history"
    >
      <GreenFitOutOnHoldHistory />
    </div>
  </div>
</template>

<script>
import { getTemplates } from '@/api/settings/request'
import HKLButton from '@/controls/Button/HKLButton'
import TenantSubmissionDocuments from '@/modules/tenantProjects/submissions/TenantSubmissionDocuments.vue'
import HkldDropzone from '@/controls/HkldDropzone'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight'
import HKLGrayHint from '@/controls/HKLGrayHint'
import { useStoreGetters } from '@/store/index'
import { templateTitle, templateType } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import lodash from 'lodash'
import np from 'number-precision'
import { computed, inject, unref } from 'vue-demi'
import { updateGreenFitoutSubmission } from '../../../api/tenantProjects/greenFitout.request'
import DescriptionTinymceEditor from '../DescriptionTinymceEditor.component'
import { DEPENDENCY_KEYS, TAB_TYPE } from '../consts'
import GreenFitOutHistory from './GreenFitOutHistory'
import GreenFitOutOnHoldHistory from './GreenFitOutOnHoldHistory'
import { GREEN_FITOUT_FOLDER_STATUS, GREEN_FITOUT_HISTORY_ACTION_TYPE, GREEN_FITOUT_HISTORY_TYPE, GREEN_FITOUT_STATUS, GREEN_FITOUT_SUB_TAB, GREEN_INJECTION_KEYS } from './consts'
import {
  useCurrentFolderMap,
  useCurrentGreenFitout,
  useCurrentGreenFitoutStatus,
  useGreenFitoutReviewers,
  useGreenFitoutCurrentReviewer,
  useGreenFitoutPerms,
  useRefreshCurrentGreenFitout,
} from './hook'
import { buildGreenFitOutHistory } from './utils'
import { useCommentAttachmentAttrs } from './GreenFitOutComments.hook'

export default {
  components: {
    HKLButton,
    HkldDropzone,
    HKLLeftRight,
    HKLGrayHint,
    TenantSubmissionDocuments,
    GreenFitOutOnHoldHistory,
    GreenFitOutHistory,
    DescriptionTinymceEditor,
  },
  setup () {
    const userStore = useStoreGetters('user', ['isHKlandUser', 'isTenant', 'currentUser'])
    const { currentTenantProject } = useStoreGetters('tenantProject', ['currentTenantProject'])
    const currentGreenFitout = useCurrentGreenFitout()
    const commentAttachmentAttrs = useCommentAttachmentAttrs()
    const folderMap = useCurrentFolderMap()
    const refresh = useRefreshCurrentGreenFitout()
    const { isCompleted, isUnderReview, isDraft } = useCurrentGreenFitoutStatus()
    const { canProjectRead, canCommentOnGreenFitout, canViewPoint, canPreview } = useGreenFitoutPerms()
    const { isCurrentReviewer, currentReviewer } = useGreenFitoutCurrentReviewer()
    const actionType = [GREEN_FITOUT_HISTORY_ACTION_TYPE.NEXT_REVIEWER, GREEN_FITOUT_HISTORY_ACTION_TYPE.START_LETTER]
    const reviewers = useGreenFitoutReviewers(actionType)
    const lastReviewedBy = computed(() => {
      const users = unref(reviewers)
      if (users.length <= 1) return
      return {
        displayName: users[0].displayName,
        createdAt: dateUtil.format(users[0].createdAt, 'DD MMM YY HH:mm'),
      }
    })
    const underReviewStartOn = computed(() => {
      const users = unref(reviewers)
      if (users.length === 0) return
      return dateUtil.format(users[users.length - 1].createdAt, 'DD MMM YY HH:mm')
    })
    const disableStartBtn = computed(() => {
      const map = unref(folderMap)
      return map.length === 0 || map.some(item => item.status !== GREEN_FITOUT_FOLDER_STATUS.COMPLETED)
    })
    const points = computed(() => {
      const list = unref(folderMap)
      return list.reduce((nums, item) => {
        item.sectionContent.forEach(subfolder => nums.push(subfolder.point))
        return nums
      }, [])
    })
    const noHasPoint = computed(() => {
      return !unref(points).some(point => typeof point === 'number')
    })
    const totalPoints = computed(() => {
      return np.plus(...unref(points).map((point) => Number(point || 0)))
    })
    const canViewContent = computed(() => {
      return canCommentOnGreenFitout.value || canPreview.value
    })
    const activeTabType = inject(DEPENDENCY_KEYS.TAB_TYPE)
    const activeSubTabType = inject(GREEN_INJECTION_KEYS.ACTIVE_SUB_TAB)
    return {
      reviewers,
      isPaneActivated: computed(() => {
        return unref(activeTabType) === TAB_TYPE.GREEN_FIT_OUT && unref(activeSubTabType) === GREEN_FITOUT_SUB_TAB.COMMENT
      }),
      ...userStore,
      commentAttachmentAttrs,
      currentGreenFitout,
      shouldShowTips: computed(() => {
        return (isDraft.value || isUnderReview.value) && canProjectRead.value && !canViewContent.value
      }),
      canCommentOnGreenFitout,
      canViewPoint,
      canViewContent,
      isCurrentReviewer,
      noHasPoint,
      totalPoints,
      disableStartBtn,
      isCompleted,
      isUnderReview,
      lastReviewedBy,
      currentReviewer,
      underReviewStartOn,
      startLetter: async () => {
        const greenFitOutDefaultDescList = await getTemplates(templateType.GREEN_FITOUT)
        const descriptionRow = greenFitOutDefaultDescList.find(item => item.title === templateTitle.Description) || ''
        const companyName = lodash.get(currentTenantProject.value.tenancy, 'company.name', '')
        const description = lodash.template(descriptionRow.content)({ companyName })
        const addHistoryList = [buildGreenFitOutHistory(
          GREEN_FITOUT_HISTORY_TYPE.SUBMISSION,
          GREEN_FITOUT_HISTORY_ACTION_TYPE.START_LETTER,
        )]
        await updateGreenFitoutSubmission(unref(currentGreenFitout).id, {
          managedBy: unref(userStore.currentUser).id,
          status: GREEN_FITOUT_STATUS.UNDER_REVIEW,
          comments: {
            description,
          },
          addHistoryList,
          lock: unref(currentGreenFitout).lock,
        })
        await refresh()
      },
    }
  },
}
</script>
<style lang="scss" scoped>
.green-fitout-comments {
  &__title {
    margin-bottom: 15px;
  }
  &__review-status {
    text-align: right;
    .hkl-gray-hint {
      font-size: 11px;
    }
  }
  &__tenant-title {
    color: #0c3a58;
    border: 1px solid #e0e1e4;
    padding: 9px 14px 11px 17px;
    align-items: center
  }
  &_description-input {
    width: 100%;
    &-display {
      width: 100%;
      text-align: left;
    }
  }
  &__attachments {
    margin-top: 10px;
    width: 100%;
  }
  .edit-attachments {
    width: 100%;
  }
  .tip__text.only-read {
    font-style: normal;
    text-align: center;
  }
  .fit-out__history {
    margin-top: 10px;
  }
}
</style>
